/**
 * Follower Benefits Preview
 * Shows what benefits are available when following a user
 */

import {
  FOLLOWER_BENEFITS,
  type FollowerPermissions,
  followerBenefitsCard,
  isFollowerBenefitShared,
} from '@parlay-picker/shared/permissionHelpers';
import { Check, X } from 'lucide-react';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface FollowerBenefitsPreviewProps {
  permissions: FollowerPermissions | null;
  onFollow: () => void;
  isAdminViewer: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function FollowerBenefitsPreview({
  permissions,
  onFollow,
  isAdminViewer,
}: FollowerBenefitsPreviewProps): ReactElement {
  const card = followerBenefitsCard(permissions, isAdminViewer);

  return (
    <div className="bg-theme-primary/10 border border-theme-primary/30 rounded-lg p-4 mb-6">
      <h4 className="text-sm font-semibold text-theme-primary mb-3">{card.heading}</h4>
      <div className="space-y-2 mb-4">
        {FOLLOWER_BENEFITS.map((benefit) => {
          const isAvailable = isFollowerBenefitShared(benefit.key, permissions);
          return (
            <div key={benefit.key} className="flex items-start gap-2">
              {isAvailable ? (
                <Check className="w-4 h-4 text-theme-success shrink-0 mt-0.5" />
              ) : (
                <X className="w-4 h-4 text-theme-text-tertiary shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div
                  className={`text-sm ${isAvailable ? 'text-gray-200' : 'text-theme-text-tertiary'}`}
                >
                  {benefit.label}
                  {!isAvailable && <span className="text-xs ml-1">{card.notSharedNote}</span>}
                </div>
                {isAvailable && (
                  <div className="text-xs text-theme-text-secondary">{benefit.description}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onFollow}
        disabled={card.isFollowDisabled}
        className="w-full px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface-hover disabled:text-theme-text-tertiary disabled:cursor-not-allowed text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
      >
        {card.followLabel}
      </button>
    </div>
  );
}
